/**
 * Zod schemas for API request/response validation
 * Used in both workers and frontend
 */
import { z } from 'zod';
// Rating context enum
export const RatingContextSchema = z.enum(['dating', 'business', 'social']);
export const ProfileVerticalSchema = z.enum(['dating', 'linkedin']);
export const ProfileRewriteToneSchema = z.enum(['clearer', 'warmer', 'sharper', 'confident']);
export const ProfileRewriteSectionSchema = z.enum(['bio', 'headline', 'about', 'banner']);
export const ProfileAnalysisSignalSchema = z.enum(['strong', 'solid', 'needs_work']);
export const ProfileAuditAreaSchema = z.enum([
    'headshot',
    'headline',
    'about',
    'banner',
    'outfit',
    'bio',
    'prompts',
    'photo_order',
]);
export const ProfileProjectSectionSchema = z.enum([
    'bio',
    'prompt-1',
    'prompt-2',
    'prompt-3',
    'headline',
    'about',
    'banner',
]);
// Rate request schema
export const RateRequestSchema = z.object({
    objectKey: z.string().min(1),
    context: RatingContextSchema,
});
// Context attributes schema
export const ContextAttributesSchema = z.object({
    attractiveness: z.number().int().min(0).max(100).optional(),
    trustworthiness: z.number().int().min(0).max(100).optional(),
    likability: z.number().int().min(0).max(100).optional(),
    competence: z.number().int().min(0).max(100).optional(),
    influence: z.number().int().min(0).max(100).optional(),
    fun: z.number().int().min(0).max(100).optional(),
    confidence: z.number().int().min(0).max(100).optional(),
    authenticity: z.number().int().min(0).max(100).optional(),
});
export const OutfitRecommendationSchema = z.object({
    title: z.string().min(1).max(120),
    detail: z.string().min(1).max(400),
    suggestion: z.string().min(1).max(300),
    priority: z.enum(['high', 'medium', 'low']),
});
export const OutfitReviewSchema = z.object({
    signal: z.enum(['strong', 'mixed', 'needs_work']),
    summary: z.string().min(1).max(400),
    recommendations: z.array(OutfitRecommendationSchema).max(5),
});
export const ProfileSuggestionSchema = z.object({
    area: z.enum([
        'lead_photo',
        'photo_order',
        'bio',
        'prompt',
        'headline',
        'about',
        'banner',
        'outfit',
    ]),
    suggestion: z.string().min(1).max(300),
});
export const ProfileRewriteRequestSchema = z.object({
    vertical: ProfileVerticalSchema,
    sectionId: ProfileRewriteSectionSchema,
    tone: ProfileRewriteToneSchema,
    currentValue: z.string().max(4000).optional().default(''),
    goal: z.string().min(1).max(400),
    suggestions: z.array(z.string().min(1).max(300)).max(8).optional(),
    outfitSummary: z.string().min(1).max(400).optional(),
});
export const ProfileRewriteResponseSchema = z.object({
    sectionId: ProfileRewriteSectionSchema,
    tone: ProfileRewriteToneSchema,
    content: z.string().min(1).max(4000),
    rationale: z.string().min(1).max(600),
    highlights: z.array(z.string().min(1).max(200)).max(5),
    polishChecklist: z.array(z.string().min(1).max(200)).max(5),
});
export const ProfileAnalysisIssueSchema = z.object({
    issue: z.string().min(1).max(120),
    detail: z.string().min(1).max(400),
    suggestion: z.string().min(1).max(300),
    priority: z.enum(['high', 'medium', 'low']),
});
export const ProfileAnalysisRequestSchema = z.object({
    vertical: ProfileVerticalSchema,
    sectionId: ProfileRewriteSectionSchema,
    currentValue: z.string().min(1).max(4000),
    goal: z.string().min(1).max(400),
    suggestions: z.array(z.string().min(1).max(300)).max(8).optional(),
    outfitSummary: z.string().min(1).max(400).optional(),
});
export const ProfileAnalysisResponseSchema = z.object({
    sectionId: ProfileRewriteSectionSchema,
    score: z.number().int().min(0).max(100),
    signal: ProfileAnalysisSignalSchema,
    summary: z.string().min(1).max(600),
    strengths: z.array(z.string().min(1).max(200)).max(5),
    issues: z.array(ProfileAnalysisIssueSchema).max(5),
    recommendedTone: ProfileRewriteToneSchema,
    rewriteRecommendation: z.string().min(1).max(300),
    analyzedAt: z.string().datetime(),
});
export const ProfileAuditRequestSectionSchema = z.object({
    id: ProfileProjectSectionSchema,
    label: z.string().min(1).max(80),
    value: z.string().max(4000),
});
export const ProfileAuditRequestSchema = z.object({
    vertical: ProfileVerticalSchema,
    goal: z.string().min(1).max(400),
    currentScore: z.number().int().min(0).max(100).nullable().optional(),
    bestScore: z.number().int().min(0).max(100).nullable().optional(),
    profileSections: z.array(ProfileAuditRequestSectionSchema).min(1).max(8),
    profileSuggestions: z.array(z.string().min(1).max(300)).max(8).optional(),
    outfitSummary: z.string().min(1).max(400).optional(),
});
export const ProfileAuditSectionSummarySchema = z.object({
    area: ProfileAuditAreaSchema,
    label: z.string().min(1).max(80),
    score: z.number().int().min(0).max(100),
    signal: ProfileAnalysisSignalSchema,
    summary: z.string().min(1).max(400),
});
export const ProfileAuditBlockerSchema = z.object({
    area: ProfileAuditAreaSchema,
    title: z.string().min(1).max(120),
    detail: z.string().min(1).max(400),
    recommendation: z.string().min(1).max(300),
    priority: z.enum(['high', 'medium', 'low']),
});
export const ProfileAuditMissingAssetSchema = z.object({
    area: ProfileAuditAreaSchema,
    label: z.string().min(1).max(120),
    detail: z.string().min(1).max(300),
});
export const ProfileAuditActionSchema = z.object({
    id: z.string().min(1).max(120),
    area: ProfileAuditAreaSchema,
    title: z.string().min(1).max(120),
    detail: z.string().min(1).max(300),
    priority: z.enum(['high', 'medium', 'low']),
});
export const ProfileAuditRewriteTargetSchema = z.object({
    sectionId: ProfileRewriteSectionSchema,
    tone: ProfileRewriteToneSchema,
    reason: z.string().min(1).max(300),
});
export const ProfileAuditResponseSchema = z.object({
    vertical: ProfileVerticalSchema,
    overallScore: z.number().int().min(0).max(100),
    signal: ProfileAnalysisSignalSchema,
    summary: z.string().min(1).max(600),
    consistencySummary: z.string().min(1).max(600),
    strengths: z.array(z.string().min(1).max(200)).max(5),
    sectionSummaries: z.array(ProfileAuditSectionSummarySchema).min(1).max(8),
    topBlockers: z.array(ProfileAuditBlockerSchema).max(5),
    missingAssets: z.array(ProfileAuditMissingAssetSchema).max(5),
    nextActions: z.array(ProfileAuditActionSchema).max(5),
    suggestedRewriteTargets: z.array(ProfileAuditRewriteTargetSchema).max(4),
    analyzedAt: z.string().datetime(),
});
// LLM Score response schema
export const LLMScoreSchema = z.object({
    model: z.string(),
    score: z.number().int().min(0).max(100),
    summary: z.string().min(1).max(500),
    traits: z.array(z.string()).max(10),
    suggestions: z.array(z.string()).max(5).optional(),
    bestUseCase: z.string().max(200).optional(),
    rawResponse: z.unknown().optional(),
    error: z.string().optional(),
    profileSuggestions: z.array(ProfileSuggestionSchema).max(5).optional(),
    outfitReview: OutfitReviewSchema.optional(),
});
// Rating result schema
export const RatingResultSchema = z.object({
    model: z.string(),
    overallScore: z.number().int().min(0).max(100),
    attributes: ContextAttributesSchema,
    summary: z.string(),
    suggestions: z.array(z.string()),
    bestUseCase: z.string(),
    traits: z.array(z.string()),
    context: RatingContextSchema,
    profileSuggestions: z.array(ProfileSuggestionSchema).max(5).optional(),
    outfitReview: OutfitReviewSchema.optional(),
});
// Aggregated rating schema
export const AggregatedRatingSchema = z.object({
    photoId: z.string().uuid(),
    compositeScore: z.number().int().min(0).max(100),
    context: RatingContextSchema,
    ratings: z.array(RatingResultSchema),
    modelCount: z.number().int(),
    createdAt: z.string().datetime(),
});
// User schema
export const UserSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    name: z.string().optional(),
    avatarUrl: z.string().url().optional(),
    credits: z.number().int().min(0),
    createdAt: z.string().datetime(),
});
// Credit transaction schema
export const CreditTransactionSchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    amount: z.number().int(),
    type: z.enum(['grant', 'purchase', 'usage', 'refund']),
    description: z.string(),
    photoId: z.string().uuid().optional(),
    stripeEventId: z.string().optional(),
    createdAt: z.string().datetime(),
});
// Valid credit packages: 5 ($5), 10 ($10), 25 ($20)
const VALID_CREDIT_AMOUNTS = [5, 10, 25];
// Purchase request schema
export const PurchaseRequestSchema = z.object({
    amount: z.number().int().refine(val => VALID_CREDIT_AMOUNTS.includes(val), {
        message: 'Invalid credit amount. Valid options: 5, 10, 25.',
    }),
    description: z.string().min(1).max(200),
});
// Purchase response schema
export const PurchaseResponseSchema = z.object({
    success: z.literal(true),
    credits: z.number().int().min(0),
    message: z.string(),
});
// Error response schema
export const ErrorResponseSchema = z.object({
    error: z.string(),
    code: z.string().optional(),
});
